import { Link } from "react-router-dom";
import {
  BarChart3,
  Package,
  FolderOpen,
  Tag,
  Ticket,
  ShoppingBag,
  Users,
  Hash,
  Activity,
  UserCog,
  FileText,
  ChevronDown,
  X
} from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "./ui/Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/Collapsible";

interface SidebarProps {
  currentPage: string;
  onPageChange?: (page: string) => void;
  user?: any;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  isMobileOpen?: boolean;
  onCloseMobile?: () => void;
}

const navigationItems = [
  { icon: BarChart3, label: "Dashboard", key: "dashboard" },
  { icon: BarChart3, label: "Relatórios", key: "reports" },
  { icon: Package, label: "Produtos", key: "produtos" },
  { icon: FolderOpen, label: "Categorias", key: "categorias" },
  { icon: Tag, label: "Marcas", key: "marcas" },
  { icon: Ticket, label: "Cupons", key: "cupons" },
  { icon: ShoppingBag, label: "Vendas", key: "vendas" },
  { icon: Users, label: "Clientes", key: "clientes" },
  { icon: Hash, label: "Etiquetas", key: "tags" },
  { icon: Activity, label: "Atividades", key: "atividades" },
  { icon: UserCog, label: "Administradores", key: "admins" },
];

const ALLOWED_EMAILS_REPORTS = [
  'contato@foradocloset.com.br',
  'ronald@ronald.com',
  'ariadne.foradocloset@gmail.com'
];


const ALLOWED_EMAILS_DASHBOARD = [
  'contato@foradocloset.com.br',
  'ronald@ronald.com'
];

const ALLOWED_EMAILS_ATIVIDADES = [
  'ronald@ronald.com',
  'contato@foradocloset.com.br'
];

export function Sidebar({
  currentPage,
  user,
  isCollapsed,
  onToggleCollapse,
  isMobileOpen,
  onCloseMobile
}: SidebarProps) {
  const conteudoKeys = ["influencers", "blog", "banner", "page", "configuracoes-loja"];
  const [isConteudoOpen, setIsConteudoOpen] = useState(() => conteudoKeys.includes(currentPage));

  // Quando currentPage mudar, abre o collapsible se um child estiver ativo
  useEffect(() => {
    if (conteudoKeys.includes(currentPage)) {
      setIsConteudoOpen(true);
    }
  }, [currentPage]);

  return (
    <>
      {/* Overlay para mobile */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onCloseMobile}
        />
      )}

      <aside className={`
        ${isCollapsed ? 'w-20' : 'w-64'} 
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        bg-white border-r h-screen flex flex-col transition-all duration-300 ease-in-out
        fixed top-0 z-50 flex-shrink-0
      `}>
        {/* Logo Section */}
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center space-x-3 overflow-hidden">
            <img
              src='/assets/logo.png'
              alt="Fora do Closet Logo"
              className="w-10 h-10 min-w-[40px] object-contain"
            />
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <h1 className="text-lg font-semibold text-gray-900 truncate">FORA DO CLOSET</h1>
                <p className="text-[10px] text-gray-600 truncate">
                  Authentic Second Hand Luxury
                </p>
              </div>
            )}
          </div>
          {/* Botão de fechar Mobile */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-gray-500"
            onClick={onCloseMobile}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation Section */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {navigationItems.map((item) => {
            // Restrição para o menu de administradores
            if (item.key === 'dashboard') {
              if (!user || !ALLOWED_EMAILS_DASHBOARD.includes(user.email)) {
                return null;
              }
            }

            // Restrição para o menu de relatórios
            if (item.key === 'reports') {
              if (!user || !ALLOWED_EMAILS_REPORTS.includes(user.email)) {
                return null;
              }
            }

            // Restrição para o menu de atividades
            if (item.key === 'atividades') {
              if (!user || !ALLOWED_EMAILS_ATIVIDADES.includes(user.email)) {
                return null;
              }
            }


            const Icon = item.icon;
            const isActive = currentPage === item.key;
            return (
              <Button
                key={item.label}
                asChild
                variant={isActive ? "secondary" : "ghost"}
                title={isCollapsed ? item.label : ""}
                className={`w-full ${isCollapsed ? 'justify-center px-0' : 'justify-start'} font-medium ${isActive
                  ? "bg-[#D4C070] text-white hover:bg-[#D4C070] border-none"
                  : "text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070]"
                  }`}
              >
                <Link to={`/${item.key}`} onClick={() => onCloseMobile?.()}>
                  <Icon className={`${isCollapsed ? 'mr-0' : 'mr-3'} h-4 w-4`} />
                  {!isCollapsed && item.label}
                </Link>
              </Button>
            );
          })}

          <Collapsible
            className="w-full"
            open={isConteudoOpen}
            onOpenChange={setIsConteudoOpen}
          >
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                title={isCollapsed ? "Conteúdo" : ""}
                className={`w-full ${isCollapsed ? 'justify-center px-0' : 'justify-between'} text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] font-medium`}
              >
                <div className="flex items-center">
                  <FileText className={`${isCollapsed ? 'mr-0' : 'mr-3'} h-4 w-4`} />
                  {!isCollapsed && "Conteúdo"}
                </div>
                {!isCollapsed && (
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isConteudoOpen ? 'rotate-180' : ''}`} />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className={`space-y-1 ${isCollapsed ? 'ml-0' : 'ml-7'} mt-1`}>
              {[
                { label: "Influencers", key: "influencers" },
                { label: "Blog", key: "blog" },
                { label: "Banner", key: "banner" },
                { label: "Páginas", key: "page" },
                { label: "Configurações", key: "configuracoes-loja" }
              ].map((subItem) => (
                <Button
                  key={subItem.key}
                  asChild
                  title={isCollapsed ? subItem.label : ""}
                  variant="ghost"
                  className={`w-full ${isCollapsed ? 'justify-center px-0' : 'justify-start'} text-sm font-medium ${currentPage === subItem.key
                    ? "bg-[#D4C070] text-white hover:bg-[#D4C070]"
                    : "text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070]"
                    }`}
                >
                  <Link to={`/${subItem.key}`} onClick={() => onCloseMobile?.()}>
                    {isCollapsed ? subItem.label.charAt(0) : subItem.label}
                  </Link>
                </Button>
              ))}
            </CollapsibleContent>
          </Collapsible>
        </nav>

        {/* Toggle Button Desktop */}
        <div className="p-4 border-t hidden lg:block">
          <Button
            variant="ghost"
            className="w-full justify-center text-gray-500 hover:text-[#D4C070]"
            onClick={onToggleCollapse}
          >
            {isCollapsed ? (
              <ChevronDown className="h-5 w-5 -rotate-90" />
            ) : (
              <div className="flex items-center space-x-2">
                <ChevronDown className="h-5 w-5 rotate-90" />
                <span className="text-xs">Recolher</span>
              </div>
            )}
          </Button>
        </div>
      </aside>
    </>
  );
}
